import {useState, FormEvent} from 'react'
import {useSearchParams} from 'react-router-dom'
import {BRAND, CONTACT, CONTACT_FORM, BED_CATEGORIES} from './site'
import {Container, PageHero, AreaMap} from './ui'
import {api} from '../../services/api'

const InfoRow: React.FC<{icon: string; label: string; children: React.ReactNode}> = ({icon, label, children}) => (
  <div style={{display: 'flex', gap: 14, marginBottom: 22}}>
    <div style={{width: 44, height: 44, borderRadius: 12, background: 'rgba(3,149,64,0.1)', color: BRAND.green, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0}}>{icon}</div>
    <div>
      <div style={{fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: BRAND.muted, marginBottom: 3}}>{label}</div>
      <div style={{fontSize: 16, color: BRAND.ink, lineHeight: 1.5}}>{children}</div>
    </div>
  </div>
)

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  fontSize: 15,
  color: BRAND.ink,
  background: '#f9fafb',
  outline: 'none',
  boxSizing: 'border-box',
}

const ContactPage = () => {
  // A "Join Waitlist" link from an unavailable bed arrives with ?waitlist=<code>.
  // Pre-fill the message so the owner sees exactly which bed they want.
  const [params] = useSearchParams()
  const waitlistCode = params.get('waitlist')
  const waitlistLabel = waitlistCode ? BED_CATEGORIES.find((c) => c.codes.includes(waitlistCode))?.label : null
  const waitlistPrefill = waitlistCode
    ? `I'd like to join the waitlist for Bed ${waitlistCode}${waitlistLabel ? ` (${waitlistLabel})` : ''}. Please notify me when it becomes available.`
    : ''

  const [form, setForm] = useState({name: '', email: '', message: waitlistPrefill})
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)

  // Submit into the Google Form (responses show in admin Integrations) AND into
  // our backend, which emails the sender an acknowledgment and forwards the
  // message to the owner. The Google Form's CORS hides its response, so we post
  // no-cors and optimistically confirm (Google records it either way); the
  // backend call is best-effort so a mail hiccup never blocks the confirmation.
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSending(true)
    try {
      const data = new FormData()
      data.append(CONTACT_FORM.fields.name, form.name)
      data.append(CONTACT_FORM.fields.email, form.email)
      data.append(CONTACT_FORM.fields.message, form.message)
      await Promise.allSettled([
        fetch(CONTACT_FORM.action, {method: 'POST', mode: 'no-cors', body: data}),
        api.submitContact({name: form.name, email: form.email, message: form.message}),
      ])
      setSent(true)
      setForm({name: '', email: '', message: ''})
    } finally {
      setSending(false)
    }
  }

  return (
    <div>
      <PageHero title='Contact us' sub="Questions about a stay or a booking? We're happy to help." />

      <section style={{padding: '64px 0 72px'}}>
        <Container>
          <div className='row g-5'>
            <div className='col-12 col-lg-5'>
              <div style={{background: 'linear-gradient(160deg, #f1faf4, #ffffff)', border: '1px solid #e6f3ea', borderRadius: 18, padding: '30px 28px', height: '100%', boxShadow: '0 6px 18px rgba(27,27,41,0.04)'}}>
                <h2 style={{fontSize: 24, fontWeight: 800, color: BRAND.ink, margin: '0 0 4px'}}>Get in touch</h2>
                <p style={{color: BRAND.muted, fontSize: 14, margin: '0 0 24px'}}>We typically reply within a day.</p>
                <InfoRow icon='📍' label='Location'>{CONTACT.locationBlurb}<br /><span style={{color: BRAND.muted, fontSize: 14}}>Exact address is shared with approved renters after deposit and approval.</span></InfoRow>
                <InfoRow icon='📞' label='Phone'>{CONTACT.phone ?? 'Coming soon'}</InfoRow>
                <InfoRow icon='✉️' label='Email'>{CONTACT.email ?? 'Use the form below — direct email coming soon.'}</InfoRow>
                <InfoRow icon='🕑' label='Reservations'>{CONTACT.reserveNote}</InfoRow>
              </div>
            </div>

            <div className='col-12 col-lg-7'>
              {sent ? (
                <div style={{background: '#fff', border: '1px solid #bbe6cd', borderRadius: 18, padding: '40px 32px', boxShadow: '0 10px 30px rgba(27,27,41,0.05)', textAlign: 'center'}}>
                  <div style={{fontSize: 44, marginBottom: 10}}>✅</div>
                  <h2 style={{fontSize: 22, fontWeight: 800, color: BRAND.ink, margin: '0 0 6px'}}>Message sent!</h2>
                  <p style={{color: BRAND.muted, fontSize: 15, margin: '0 0 20px'}}>Thanks for reaching out — we'll get back to you soon.</p>
                  <button onClick={() => setSent(false)} className='ccp-cta' style={{fontWeight: 700, fontSize: 15, color: '#fff', background: BRAND.green, border: 'none', borderRadius: 12, padding: '12px 26px', cursor: 'pointer'}}>
                    Send another
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} style={{background: '#fff', border: '1px solid #eef0f4', borderRadius: 18, padding: '30px 28px', boxShadow: '0 10px 30px rgba(27,27,41,0.05)'}}>
                  {waitlistCode && (
                    <div style={{display: 'flex', gap: 10, alignItems: 'flex-start', background: 'rgba(3,149,64,0.07)', border: '1px solid rgba(3,149,64,0.2)', borderRadius: 12, padding: '12px 14px', marginBottom: 18}}>
                      <span style={{fontSize: 18, lineHeight: 1.3}}>📋</span>
                      <div style={{fontSize: 14, color: BRAND.ink, lineHeight: 1.5}}>
                        Joining the waitlist for <strong>Bed {waitlistCode}{waitlistLabel ? ` · ${waitlistLabel}` : ''}</strong>. Add your details below and we'll notify you the moment it opens up.
                      </div>
                    </div>
                  )}
                  <div className='row g-3'>
                    <div className='col-md-6'>
                      <label style={{display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6}}>Name</label>
                      <input style={inputStyle} value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required />
                    </div>
                    <div className='col-md-6'>
                      <label style={{display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6}}>Email</label>
                      <input type='email' style={inputStyle} value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required />
                    </div>
                    <div className='col-12'>
                      <label style={{display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6}}>Message</label>
                      <textarea style={{...inputStyle, minHeight: 130, resize: 'vertical'}} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} required />
                    </div>
                  </div>
                  <button type='submit' disabled={sending} className='ccp-cta' style={{marginTop: 18, fontWeight: 700, fontSize: 15, color: '#fff', background: BRAND.green, border: 'none', borderRadius: 12, padding: '13px 28px', cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.7 : 1}}>
                    {sending ? 'Sending…' : 'Send message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </Container>
      </section>

      {/* Map — general area only; exact building is withheld until approval + payment. */}
      <section style={{padding: '0 0 72px'}}>
        <Container>
          <AreaMap query={CONTACT.mapsQuery} height={380} />
        </Container>
      </section>
    </div>
  )
}

export {ContactPage}
